import { eventChannel } from 'redux-saga';
import { takeLatest, call, put, take } from 'redux-saga/effects';
import { get } from 'lodash-es';
import axiosLib from 'axios';
import apiCall from '../../utils/apiCall';
import axios from '../../utils/axios';
import { requestFail, requestPending, requestSuccess } from '../../utils/status';
import { uploadFileToGcsResumable } from '../../views/documents/gcsResumableUpload';
import {
  isDocumentVideoFile,
  RESOURCE_CENTER_FILE_TYPE
} from '../../views/documents/constants';
import {
  CREATE_FOLDER,
  DELETE_DOCUMENT,
  DELETE_FOLDER,
  GET_DOCUMENT,
  GET_DOCUMENTS,
  GET_FOLDER_DOCUMENTS,
  GET_FOLDER_OPTIONS,
  MOVE_DOCUMENT,
  UPDATE_DOCUMENT,
  UPDATE_FOLDER,
  UPLOAD_DOCUMENT,
  UPLOAD_DOCUMENT_PROGRESS
} from '../constants';

const getDocuments = apiCall({
  type: GET_DOCUMENTS,
  method: 'GET',
  path: '/docs'
});

const getDocument = apiCall({
  type: GET_DOCUMENT,
  method: 'GET',
  path: ({ id }) => `/docs/${id}`
});

const updateDocument = apiCall({
  type: UPDATE_DOCUMENT,
  method: 'PUT',
  path: ({ id }) => `/docs/${id}`,
  isFile: true
});

const deleteDocument = apiCall({
  type: DELETE_DOCUMENT,
  method: 'DELETE',
  path: ({ id }) => `/docs/${id}`
});

const getFolderDocuments = apiCall({
  type: GET_FOLDER_DOCUMENTS,
  method: 'GET',
  path: ({ id }) => `/folders/${id}/documents`
});

const createFolder = apiCall({
  type: CREATE_FOLDER,
  method: 'POST',
  path: '/folders'
});

const updateFolder = apiCall({
  type: UPDATE_FOLDER,
  method: 'PATCH',
  path: ({ id }) => `/folders/${id}`
});

const deleteFolder = apiCall({
  type: DELETE_FOLDER,
  method: 'DELETE',
  path: ({ id }) => `/folders/${id}`
});

const moveDocument = apiCall({
  type: MOVE_DOCUMENT,
  method: 'PATCH',
  path: ({ id }) => `/docs/${id}/folder`
});

/**
 * Collect folders for a building (and shared) by paging GET /docs.
 * Folders are returned before root docs, so we stop once a page has no folders.
 */
function* getFolderOptionsSaga(action) {
  const {
    params,
    success: successPayload,
    fail: failPayload,
    finally: finallyPayload
  } = action.payload || {};

  const idToken = localStorage.getItem('idToken');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`
  };

  try {
    yield put({ type: requestPending(GET_FOLDER_OPTIONS) });

    const folders = [];
    let page = 1;
    const maxPages = 50;

    while (page <= maxPages) {
      const res = yield call(axios.request, {
        url: '/docs',
        method: 'GET',
        headers,
        params: {
          ...params,
          type: RESOURCE_CENTER_FILE_TYPE,
          page
        }
      });

      const pageFolders = res?.data?.folders || [];
      if (!pageFolders.length) {
        break;
      }

      folders.push(...pageFolders);

      if (pageFolders.length < 20) {
        break;
      }

      page += 1;
    }

    yield put({
      type: requestSuccess(GET_FOLDER_OPTIONS),
      payload: folders
    });
    successPayload && successPayload(folders);
  } catch (err) {
    const errRes = get(err, 'response', err);
    yield put({
      type: requestFail(GET_FOLDER_OPTIONS),
      payload: errRes
    });
    failPayload && failPayload(errRes);
  } finally {
    finallyPayload && finallyPayload();
  }
}

/**
 * Bridges axios multipart upload progress into redux-saga via an event channel.
 * Emits { progress } updates, then { done: true, response } or { error }.
 */
const createMultipartUploadChannel = (options) =>
  eventChannel((emitter) => {
    let closed = false;
    const cancelSource = axiosLib.CancelToken.source();

    axios
      .request({
        ...options,
        cancelToken: cancelSource.token,
        onUploadProgress: (event) => {
          if (closed || !event.total) {
            return;
          }
          emitter({
            progress: Math.min(1, Math.max(0, event.loaded / event.total)),
            stalled: false
          });
        }
      })
      .then((response) => {
        if (!closed) {
          emitter({ progress: 1, stalled: false, done: true, response });
        }
      })
      .catch((error) => {
        if (!closed && !axiosLib.isCancel(error)) {
          emitter({ error });
        }
      });

    return () => {
      closed = true;
      cancelSource.cancel('Upload cancelled');
    };
  });

/**
 * Bridges GCS upload progress callbacks into redux-saga via an event channel.
 * Emits { progress, stalled } updates, then { done: true } or { error }.
 * Does not use END so the parent saga can continue to the complete step.
 */
const createGcsUploadChannel = (uploadUrl, file, contentType) =>
  eventChannel((emitter) => {
    let closed = false;

    uploadFileToGcsResumable(uploadUrl, file, contentType, (progress, meta) => {
      if (!closed) {
        emitter({ progress, stalled: Boolean(meta?.stalled) });
      }
    })
      .then(() => {
        if (!closed) {
          emitter({ progress: 1, stalled: false, done: true });
        }
      })
      .catch((error) => {
        if (!closed) {
          emitter({ error });
        }
      });

    return () => {
      closed = true;
    };
  });

function* emitUploadProgress(event) {
  yield put({
    type: UPLOAD_DOCUMENT_PROGRESS,
    payload: {
      progress: event.progress,
      stalled: Boolean(event.stalled)
    }
  });
}

/**
 * Multipart POST /docs with axios upload progress.
 */
function* uploadDocumentMultipart({ body, params }) {
  const idToken = localStorage.getItem('idToken');
  const uploadChannel = yield call(createMultipartUploadChannel, {
    url: '/docs',
    method: 'POST',
    headers: {
      'Content-Type': 'multipart/form-data',
      Authorization: `Bearer ${idToken}`,
      'Access-Control-Allow-Origin': '*'
    },
    data: body,
    params
  });

  try {
    let response;
    while (true) {
      const event = yield take(uploadChannel);
      if (event.error) {
        throw event.error;
      }
      yield* emitUploadProgress(event);
      if (event.done) {
        response = event.response;
        break;
      }
    }
    return response;
  } finally {
    uploadChannel.close();
  }
}

/**
 * Video upload: start session → PUT to GCS upload_url → complete → GET document.
 */
function* uploadDocumentResumable({ body, file }) {
  const idToken = localStorage.getItem('idToken');
  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${idToken}`
  };

  const sessionRes = yield call(axios.request, {
    url: '/docs/upload-sessions',
    method: 'POST',
    headers,
    data: body
  });

  const session = sessionRes?.data || {};
  if (!session.upload_url || !session.object_name) {
    throw new Error('Upload session did not return upload_url/object_name');
  }
  if (!session.document_id) {
    throw new Error('Upload session did not return document_id');
  }

  const uploadChannel = yield call(
    createGcsUploadChannel,
    session.upload_url,
    file,
    session.content_type || body.content_type
  );

  try {
    while (true) {
      const event = yield take(uploadChannel);
      if (event.error) {
        throw event.error;
      }
      yield* emitUploadProgress(event);
      if (event.done) {
        break;
      }
    }
  } finally {
    uploadChannel.close();
  }

  const completeRes = yield call(axios.request, {
    url: '/docs/upload-sessions/complete',
    method: 'POST',
    headers,
    data: {
      ...body,
      object_name: session.object_name,
      document_id: session.document_id
    }
  });

  const newId = completeRes?.data?.new_id;
  if (!newId) {
    throw new Error('Upload complete did not return new_id');
  }

  return yield call(axios.request, {
    url: `/docs/${newId}`,
    method: 'GET',
    headers,
    params: { type: RESOURCE_CENTER_FILE_TYPE }
  });
}

/**
 * Unified document upload. Branches on file type:
 * - video/mp4 → GCS resumable session flow
 * - everything else → multipart POST /docs
 */
function* uploadDocument(action) {
  const {
    body,
    file,
    params,
    success: successPayload,
    fail: failPayload,
    finally: finallyPayload
  } = action.payload || {};

  try {
    yield put({ type: requestPending(UPLOAD_DOCUMENT) });

    const isVideo = isDocumentVideoFile(file);
    const response = isVideo
      ? yield call(uploadDocumentResumable, { body, file })
      : yield call(uploadDocumentMultipart, { body, params });

    yield put({
      type: requestSuccess(UPLOAD_DOCUMENT),
      payload: response?.data
    });

    if (isVideo) {
      yield put({
        type: requestSuccess(GET_DOCUMENT),
        payload: response?.data
      });
    }

    successPayload && successPayload(response);
  } catch (err) {
    const errRes = get(err, 'response', err);
    yield put({
      type: requestFail(UPLOAD_DOCUMENT),
      payload: errRes
    });
    failPayload && failPayload(errRes);
  } finally {
    finallyPayload && finallyPayload();
  }
}

export default function* documentsSaga() {
  yield takeLatest(GET_DOCUMENTS, getDocuments);
  yield takeLatest(GET_DOCUMENT, getDocument);
  yield takeLatest(UPLOAD_DOCUMENT, uploadDocument);
  yield takeLatest(UPDATE_DOCUMENT, updateDocument);
  yield takeLatest(DELETE_DOCUMENT, deleteDocument);
  yield takeLatest(GET_FOLDER_DOCUMENTS, getFolderDocuments);
  yield takeLatest(GET_FOLDER_OPTIONS, getFolderOptionsSaga);
  yield takeLatest(CREATE_FOLDER, createFolder);
  yield takeLatest(UPDATE_FOLDER, updateFolder);
  yield takeLatest(DELETE_FOLDER, deleteFolder);
  yield takeLatest(MOVE_DOCUMENT, moveDocument);
}
